import axios from 'axios';
import {
    ActivitiesApi,
    ActivitiesSortEnum,
    ActivityAthlete,
    ActivityPeriod,
    AthleteFilesSyncStatusResponse,
    DeepActivity,
    DeviceInfo,
} from 'oas';
import { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query';
import { useApiQueries } from 'api/use-api-queries';
import { transformActivity } from '.';
import { mockResponse, useApiQuery } from '../use-api-query';
import { useInfiniteApiQuery } from '../use-infinite-api-query';
import { TfActivity, transformActivityListItem } from './transform';
import { ActivityResponse, ActivitiesApi as InternalActivityApi } from '../../oas/internal';
import { API_NAMES } from '../constants';

export const useGetActivity: (
    ...parameters: Parameters<ActivitiesApi['getActivity']>
) => UseQueryResult<TfActivity | null> = (id, include, options = {}) =>
    useApiQuery(['activities', id, include], (apiConfig, axiosInstance) => {
        return id !== ''
            ? new ActivitiesApi(apiConfig, '', axiosInstance).getActivity(id, include, options).then((response) => {
                  if (axios.isAxiosError(response)) {
                      throw response;
                  }

                  return { ...response, data: transformActivity(response.data) };
              })
            : mockResponse({
                  id: '',
                  name: '',
                  modified_at: new Date(),
                  athletes: [],
                  periods: [],
                  athlete_count: 0,
                  period_count: 0,
                  duration: 0,
                  owner_id: '',
                  owner_name: '',
                  is_deleted: 0,
                  start_time: new Date(),
                  end_time: new Date(),
                  full_sync: false,
                  teams: [],
                  tags: [],
              });
    });

export const useGetActivities = (
    pageSize?: number,
    sort?: ActivitiesSortEnum,
    deleted?: number,
    include?: Array<'actdic_status' | 'activity_athletes'>,
    startTime?: number,
    endTime?: number,
    query?: string,
    options = {},
    tags?: Array<string>,
    teamIds?: Array<string>,
): UseInfiniteQueryResult<InfiniteData<TfActivity[]>> => {
    const myQuery = query || undefined; // prevents empty query= parameter
    return useInfiniteApiQuery(
        ['activities', pageSize, sort, deleted, include, startTime, endTime, myQuery, tags, teamIds],
        (pageParam, apiConfig, axiosInstance) =>
            new ActivitiesApi(apiConfig, '', axiosInstance)
                .getAllActivities(
                    pageParam || 1,
                    pageSize,
                    sort,
                    deleted,
                    include,
                    startTime,
                    endTime,
                    myQuery,
                    tags,
                    teamIds,
                )
                .then((response) => ({ ...response, data: response.data.map(transformActivityListItem) })),
        options,
    );
};

export const useGetOverlappingActivities = (
    startTime: number,
    endTime: number,
    options = {},
): UseQueryResult<ActivityResponse[]> => {
    return useApiQuery(
        ['activities', 'overlapping', startTime, endTime],
        (apiConfig, axiosInstance) =>
            new InternalActivityApi(apiConfig, '', axiosInstance).getOverlappingActivities(startTime, endTime),
        options,
        'internal',
        API_NAMES.ACTIVITY_SERVICE,
    );
};

export const useGetPeriodsInActivity = (
    ...parameters: Parameters<ActivitiesApi['getPeriodsInActivity']>
): UseQueryResult<ActivityPeriod[]> =>
    useApiQuery(
        ['activities', 'periods', parameters],
        (apiConfig, axiosInstance) =>
            new ActivitiesApi(apiConfig, '', axiosInstance).getPeriodsInActivity(...parameters),
        {
            enabled: !!parameters[0],
        },
    );

export const useGetAthletesInActivity = (
    ...parameters: Parameters<ActivitiesApi['getAthletesInActivity']>
): UseQueryResult<ActivityAthlete[]> =>
    useApiQuery(
        ['activities', 'athletes', parameters],
        (apiConfig, axiosInstance) =>
            new ActivitiesApi(apiConfig, '', axiosInstance).getAthletesInActivity(...parameters),
        {
            enabled: !!parameters[0],
        },
    );

export const useGetDevicesInActivity = (
    ...parameters: Parameters<ActivitiesApi['getDevices']>
): UseQueryResult<DeviceInfo[]> =>
    useApiQuery(
        ['activities', 'unmapped_devices', parameters],
        (apiConfig, axiosInstance) => new ActivitiesApi(apiConfig, '', axiosInstance).getDevices(...parameters),
        {
            enabled: !!parameters[0],
        },
    );

export const useGetDeepActivity = (
    ...parameters: Parameters<ActivitiesApi['getDeepActivity']>
): UseQueryResult<DeepActivity[]> =>
    useApiQuery(
        // The id sits in its own segment so a caller can invalidate one activity's deep query.
        // Inside `parameters` it is unreachable: keys are matched from the left, a segment at a
        // time, so anything wanting to drop a single activity would have to drop them all.
        ['activities', 'deep', parameters[0], parameters],
        (apiConfig, axiosInstance) => new ActivitiesApi(apiConfig, '', axiosInstance).getDeepActivity(...parameters),
        {
            enabled: !!parameters[0],
        },
    );

interface UseSyncStatusOptions {
    enabled?: boolean;
}

export function useGetFilesSyncStatusForAthletes(
    fromMs: number,
    toMs: number,
    athleteIds: string[],
    filetypes: string,
    options?: UseSyncStatusOptions,
) {
    const fromSeconds = Math.floor(fromMs / 1000);
    const toSeconds = Math.ceil(toMs / 1000);
    const defaultEnabled =
        !isNaN(fromSeconds) &&
        !isNaN(toSeconds) &&
        fromSeconds > 0 &&
        toSeconds >= fromSeconds &&
        athleteIds.length > 0 &&
        Boolean(filetypes);

    const queryKeys = athleteIds.map((id) => ['files-sync-status', 'athletes', fromSeconds, toSeconds, id, filetypes]);

    const results = useApiQueries<AthleteFilesSyncStatusResponse, string>(
        queryKeys,
        athleteIds,
        (apiConfig, axiosInstance, athleteId) =>
            new ActivitiesApi(apiConfig, '', axiosInstance).getFilesSyncStatusForAthlete(
                athleteId,
                fromSeconds,
                toSeconds,
                filetypes,
            ),
        {
            ...options,
            enabled: (options?.enabled ?? true) && defaultEnabled,
        },
    );
    return {
        data: results.map((result, index) => ({
            athleteId: athleteIds[index],
            data: result.data,
        })),
        isLoading: results.some((result) => result.isLoading),
        isError: results.some((result) => result.isError),
        isFetching: results.some((result) => result.isFetching),
    } as UseQueryResult<{ athleteId: string; data: AthleteFilesSyncStatusResponse }[]>;
}
